// window.LaneEmbed —— 给 embed 组件作者的轻量通信垫片(postMessage 封装)。
// spec: docs/superpowers/specs/2026-08-07-native-chat-ui-design.md 「Embed 交互 Bridge」红线 4。
//
// ┌─ 交付方式(2026-08-08 最终定案)──────────────────────────────────────────────┐
// │ 协议视为固定/稳定;垫片单一稳定端点,不版本化(不建 lane-embed@N.js)。          │
// │  1. embed_url 模式(第三方独立页,主场景):一行接入 —— 详见 docs/EMBED_SDK.md   │
// │        <script src="https://cdn.lane.chat/embed/lane-embed.js"></script>       │
// │     然后用 window.LaneEmbed。CDN 强缓存(immutable / 长 max-age)。             │
// │  2. srcdoc 模式(我们自己生成的 embed_html):widget 生成 srcdoc 时把            │
// │     LANE_EMBED_SHIM_SOURCE 直接拼进 <script>(编译期就是 widget bundle 里的     │
// │     字符串常量,零外链),window.LaneEmbed 就位。                                │
// │  3. 软降级:CDN 拉取失败 → window.LaneEmbed 不存在时 embed 内容仍能渲染(只是    │
// │     失去交互能力);bridge 分发器对"没有握手"的 embed 静默容忍,不白屏不崩。      │
// │                                                                              │
// │ widget 侧 import 的只是 LANE_EMBED_SHIM_SOURCE 字符串常量(供 srcdoc 内联);     │
// │ 垫片不在 widget 里执行(widget 跑的是 bridge 分发器,见 chat/embed-bridge.js)。 │
// │ 向后兼容唯一保险:垫片与 bridge 消息带 v:1;宿主对未知/低版本消息优雅降级不崩。   │
// └──────────────────────────────────────────────────────────────────────────┘
//
// API(embed 内容里):
//   LaneEmbed.onInit(cb)         宿主 init 到达 → cb({ theme, lang, mainColor, state })
//   LaneEmbed.onTheme(cb)        宿主主题切换 → cb({ theme })
//   LaneEmbed.notify(name, data) 上报业务事件(投票/游戏完成)→ 宿主转 postback
//   LaneEmbed.submit(data)       提交表单数据
//   LaneEmbed.resize(height)     请求高度(宿主夹 ≤480)
//   LaneEmbed.close()            请求收起
//   LaneEmbed.openUrl(url)       请求宿主按 linkPolicy 打开
//   LaneEmbed.ready()            握手(onInit 会自动触发一次,一般不用手调)
//
// ┌─ 数据边界(安全模型核心)──────────────────────────────────────────────────┐
// │ embed 拿到的只有 onInit 的 { theme, lang, mainColor, state } —— 全是非敏感展示 │
// │ 数据。宿主绝不给 embed:chat_id / visitor_id / client_id / session token /      │
// │ 访客 PII。所以 notify/submit 的 data 只放**业务字段**(如 {option:'A'}),别放  │
// │ 身份/会话字段(放了宿主也不用、不转发)。会话/访客身份 + 这条 embed 消息 id,   │
// │ 全由宿主从自己的上下文补齐后再发服务端。embed 全程不知道自己在哪个会话。        │
// └──────────────────────────────────────────────────────────────────────────┘

// createLaneEmbed 必须**完全自包含**(不引用任何模块作用域标识符),因为
// LANE_EMBED_SHIM_SOURCE 靠 createLaneEmbed.toString() 生成 —— 闭包外的变量会丢。
// 内联进 srcdoc 的、第三方拷走的、单测跑的,都是同一份函数体(零漂移)。
export function createLaneEmbed(win) {
    'use strict';
    var NS = 'lane-embed';
    var VERSION = 1;
    win = win || (typeof window !== 'undefined' ? window : null);

    var initCbs = [];
    var themeCbs = [];
    var lastInit = null;     // 最近一次 init 载荷(晚注册的 onInit 也能立即拿到)
    var readySent = false;

    function parent() {
        // sandbox iframe 的宿主永远是 window.parent;顶层(非 iframe)时退化为自身
        // (便于本地打开 demo 页调试)。
        if (!win) return null;
        return (win.parent && win.parent !== win) ? win.parent : win;
    }

    function post(payload) {
        var target = parent();
        if (!target || typeof target.postMessage !== 'function') return;
        payload.ns = NS;
        payload.v = VERSION;
        // targetOrigin '*':opaque origin 的 iframe 无法具名定址宿主;这些载荷只是
        // vote/resize/close 请求,不含敏感数据,宿主再按白名单双向校验。
        try { target.postMessage(payload, '*'); } catch (e) { /* best-effort */ }
    }

    function onHostMessage(event) {
        var data = event && event.data;
        if (!data || typeof data !== 'object' || data.ns !== NS) return;
        if (data.type === 'init') {
            lastInit = {
                theme: data.theme === 'dark' ? 'dark' : 'light',
                lang: typeof data.lang === 'string' ? data.lang : null,
                mainColor: typeof data.mainColor === 'string' ? data.mainColor : null,
                state: (data.state !== undefined ? data.state : null)
            };
            for (var i = 0; i < initCbs.length; i++) {
                try { initCbs[i](lastInit); } catch (e) { /* isolate */ }
            }
        } else if (data.type === 'theme') {
            var t = { theme: data.theme === 'dark' ? 'dark' : 'light' };
            if (lastInit) lastInit.theme = t.theme;
            for (var j = 0; j < themeCbs.length; j++) {
                try { themeCbs[j](t); } catch (e) { /* isolate */ }
            }
        }
    }

    if (win && typeof win.addEventListener === 'function') {
        win.addEventListener('message', onHostMessage);
    }

    var api = {
        ready: function () {
            if (readySent) return;
            readySent = true;
            post({ type: 'ready' });
        },
        onInit: function (cb) {
            if (typeof cb !== 'function') return;
            initCbs.push(cb);
            if (lastInit) { try { cb(lastInit); } catch (e) { /* isolate */ } }
            else api.ready();
        },
        onTheme: function (cb) {
            if (typeof cb === 'function') themeCbs.push(cb);
        },
        notify: function (name, data) {
            post({ type: 'notify', name: String(name || ''), data: data == null ? {} : data });
        },
        submit: function (data) {
            post({ type: 'submit', data: data == null ? {} : data });
        },
        resize: function (height) {
            post({ type: 'resize', height: Number(height) });
        },
        close: function () {
            post({ type: 'close' });
        },
        openUrl: function (url) {
            post({ type: 'openUrl', url: String(url || '') });
        }
    };

    return api;
}

/**
 * 垫片源码字符串。由 createLaneEmbed 源码 + 全局挂载语句拼成 —— 与被单测的
 * createLaneEmbed 是同一份(零漂移)。用途:
 *   - CDN 端点内容:scripts/gen-embed-sdk.mjs 写成 dist/embed/lane-embed.js;
 *   - srcdoc 内联:widget(embed-message.js)把它拼进 srcdoc 的 <script>。
 */
export const LANE_EMBED_SHIM_SOURCE =
    '/* Lane.Chat Embed interaction shim (window.LaneEmbed). Protocol v1. */\n'
    + createLaneEmbed.toString() + '\n'
    + 'try { if (typeof window !== "undefined") window.LaneEmbed = createLaneEmbed(window); } catch (e) {}\n';
